import { AddOutlined } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { amber, blue, green, grey, red } from "@mui/material/colors";
import { isNil } from "lodash";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Flx from "../../../../components/layout/Flx";
import ScreenContent from "../../../../components/layout/ScreenContent";
import Htag from "../../../../components/typography/Htag";
import Txt from "../../../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSetKey } from "../../../../redux/actions/loanDrilldownActions";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
  sidebarTypeToggle,
} from "../../../../redux/actions/sidebarActions";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import OrderCard from "./components/OrderCard";
import AppraisalOrdersCard from "./components/AppraisalOrdersCard";
import AppraisalOrderDetailDrawer from "../../../../components/appraisalOrders/AppraisalOrderDetailDrawer";
import { orderAppraisalDocOids } from "../../../../components/appraisalOrders/appraisalOrderUi";
import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";
import {
  isExternalSecondary,
  SECONDARY_ORDERS_KEY,
  useRefreshSecondaryOrders,
} from "../../../../components/appraisalOrders/secondaryOrderUi";
import SecondaryOrderActionDialogs from "./components/SecondaryOrderActionDialogs";

/* ─────────────────────────────────────────────
   Tab — aggregates external orders for the loan
   ─────────────────────────────────────────────
   Sources of truth in Redux:
     - Flood cert: subjectProperties[].lastFloodCertResult
     - Funding Shield: loan.fundingShieldStatus + loan.fundingShieldTransactionId
       (or loan.lastFundingShieldOrder if backend stamps a richer object).
   Other order types (appraisal / credit / title / background) aren't yet
   wired to backend — surfaced as "not ordered" placeholders for
   visibility, with disabled CTAs.
*/

// ObjectIds may arrive as strings or { $oid } objects — compare by value.
const sameOid = (a, b) => {
  const s = (v) => (v && typeof v === "object" ? v.$oid || v._id : v);
  return s(a) != null && s(a) === s(b);
};

const LoanDrilldownOrdersTab = memo(() => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const loanId = loan?._id;
  const subjectProperties = loan?.subjectProperties || [];
  const appraisalOrders = loan?.appraisalOrders;
  // Outside-party (vendor / AMC) secondary valuations. Internal-kind
  // secondaries are worked in the internal queue and already listed through
  // `appraisalOrders`, so they're left out here.
  const secondaryOrdersRaw = loan?.[SECONDARY_ORDERS_KEY];
  const secondaryOrders = useMemo(
    () => (secondaryOrdersRaw || []).filter(isExternalSecondary),
    [secondaryOrdersRaw],
  );
  // { type, order } for the open Mark placed / Mark received / Cancel modal.
  const [secondaryAction, setSecondaryAction] = useState(null);
  const refreshSecondaryOrders = useRefreshSecondaryOrders();

  const [statusCheckingId, setStatusCheckingId] = useState(null);
  const [downloadingCertId, setDownloadingCertId] = useState(null);
  // Order being previewed in the side drawer (View on an appraisal order).
  const [viewOrder, setViewOrder] = useState(null);
  const [previewingDoc, setPreviewingDoc] = useState(false);

  const {
    queryFloodOrderStatus,
    checkFundingShieldStatus,
    downloadFundingShieldCertificate,
    getAppraisalOrders,
  } = useUnderwritingHook();
  const { getFileById } = useLoanFilesHook();

  // Resolve every appraisal document on the order from the loan (an order can
  // carry several) and preview each inline. `previewingDoc` holds the oid of
  // the one being fetched so only its card shows the spinner.
  const viewAppraisalDocs = useMemo(() => {
    const docs = loan?.loanDocuments || [];
    return orderAppraisalDocOids(viewOrder)
      .map((oid) => docs.find((d) => sameOid(d?._id, oid)) || null)
      .filter(Boolean);
  }, [viewOrder, loan?.loanDocuments]);

  const handlePreviewDoc = useCallback(
    (doc) => {
      if (!loanId || !doc?.file_storage_name) return;
      const oid = String(doc?._id?.$oid || doc?._id || "");
      setPreviewingDoc(oid || true);
      getFileById({
        loanId,
        file_storage_name: doc.file_storage_name,
        fileDownloadName: doc.file_display_name,
        isFile: true,
        openInNewTab: true,
        onSuccessFn: () => setPreviewingDoc(false),
        onFailFn: () => setPreviewingDoc(false),
      });
    },
    [loanId, getFileById],
  );

  // Lazily load the loan-scoped internal-appraisal orders when this tab is
  // opened (not on every loan load). Refetches whenever the slice is MISSING
  // (`appraisalOrders` is nil) — the reducer carries it across same-loan
  // refreshes, but a fresh loan load / clear starts it nil again. The in-flight
  // ref stops a second request while one is already running; a failed fetch
  // parks `[]` so this doesn't loop.
  const appraisalFetchInFlightRef = useRef(null);
  useEffect(() => {
    if (!loanId) return;
    if (!isNil(appraisalOrders)) return;
    if (appraisalFetchInFlightRef.current === loanId) return;
    appraisalFetchInFlightRef.current = loanId;
    getAppraisalOrders({
      dealId: loanId,
      onSuccessFn: (orders) =>
        dispatch(
          loanDrilldownSetKey({
            key: "appraisalOrders",
            value: Array.isArray(orders) ? orders : [],
          }),
        ),
      onFailFn: () =>
        dispatch(loanDrilldownSetKey({ key: "appraisalOrders", value: [] })),
      onCompleteFn: () => {
        if (appraisalFetchInFlightRef.current === loanId) {
          appraisalFetchInFlightRef.current = null;
        }
      },
    });
  }, [loanId, appraisalOrders, getAppraisalOrders, dispatch]);

  // Same lazy load for the outside-party secondary valuation orders.
  const secondaryFetchInFlightRef = useRef(null);
  useEffect(() => {
    if (!loanId) return;
    if (!isNil(secondaryOrdersRaw)) return;
    if (secondaryFetchInFlightRef.current === loanId) return;
    secondaryFetchInFlightRef.current = loanId;
    refreshSecondaryOrders(loanId).finally(() => {
      if (secondaryFetchInFlightRef.current === loanId) {
        secondaryFetchInFlightRef.current = null;
      }
    });
  }, [loanId, secondaryOrdersRaw, refreshSecondaryOrders]);

  /* ── Action handlers ────────────────────────────────────────────────── */

  const openFloodCertSidebar = useCallback(
    (propertyId) => {
      if (!propertyId) return;
      dispatch(sidebarSetStateValue(propertyId));
      dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.ORDER_FLOOD_CERT));
    },
    [dispatch],
  );

  const openFundingShieldSidebar = useCallback(() => {
    dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.ORDER_FUNDING_SHIELD));
  }, [dispatch]);

  // `propertyId` (optional) pre-populates the property picker — the per-property
  // "Order review" rows pass it; the card header button opens it blank. Uses
  // sidebarTypeSet (not toggle) so clicking a second property row while the
  // panel is open re-targets it instead of closing it.
  const openInternalAppraisalSidebar = useCallback(
    (propertyId) => {
      dispatch(
        sidebarSetStateValue(
          typeof propertyId === "string" ? propertyId : null,
        ),
      );
      dispatch(
        sidebarTypeSet(loanDrilldownSidebarTypes.ORDER_INTERNAL_APPRAISAL),
      );
    },
    [dispatch],
  );

  // "Order review → Outside party" on a property row.
  const openOutsideValuationSidebar = useCallback(
    (propertyId) => {
      dispatch(
        sidebarSetStateValue(
          typeof propertyId === "string" ? propertyId : null,
        ),
      );
      dispatch(
        sidebarTypeSet(loanDrilldownSidebarTypes.ORDER_OUTSIDE_VALUATION),
      );
    },
    [dispatch],
  );

  const openSecondaryAction = useCallback(
    (type, order) => setSecondaryAction({ type, order }),
    [],
  );

  // Attached-doc chips on the order rows open the global file-preview sidebar.
  const openDocPreview = useCallback(
    (doc) => {
      if (doc) dispatch(filePreviewSetData(doc));
    },
    [dispatch],
  );

  const handleFloodStatusCheck = useCallback(
    (property) => {
      const certId = property?.lastFloodCertResult?.floodCertificationId;
      if (!loanId || !certId) return;
      setStatusCheckingId(`flood:${property._id}`);
      queryFloodOrderStatus({
        loanId,
        floodCertificationId: certId,
        onSuccessFn: (response) => {
          if (response?.subjectProperties) {
            dispatch(
              loanDrilldownSetKey({
                key: "subjectProperties",
                value: response.subjectProperties,
              }),
            );
          }
          if (response?.loanDocuments) {
            dispatch(
              loanDrilldownSetKey({
                key: "loanDocuments",
                value: response.loanDocuments,
              }),
            );
          }
        },
        onCompleteFn: () => setStatusCheckingId(null),
      });
    },
    [loanId, queryFloodOrderStatus, dispatch],
  );

  const handleFundingShieldStatusCheck = useCallback(() => {
    const txId = loan?.fundingShieldTransactionId;
    if (!loanId || !txId) return;
    setStatusCheckingId("funding_shield");
    checkFundingShieldStatus({
      loanId,
      transactionId: txId,
      onSuccessFn: (response) => {
        if (response?.loan) {
          // Backend returns the full refreshed deal — splice in the fields
          // we render against without resetting unrelated slices.
          [
            "fundingShieldStatus",
            "fundingShieldTransactionId",
            "lastFundingShieldOrder",
            "loanDocuments",
            "subjectProperties",
          ].forEach((key) => {
            if (!isNil(response.loan[key])) {
              dispatch(loanDrilldownSetKey({ key, value: response.loan[key] }));
            }
          });
        }
      },
      onCompleteFn: () => setStatusCheckingId(null),
    });
  }, [
    loanId,
    loan?.fundingShieldTransactionId,
    checkFundingShieldStatus,
    dispatch,
  ]);

  const handleFundingShieldCertDownload = useCallback(() => {
    const txId = loan?.fundingShieldTransactionId;
    if (!loanId || !txId) return;
    setDownloadingCertId("funding_shield");
    downloadFundingShieldCertificate({
      loanId,
      transactionId: txId,
      onSuccessFn: (response) => {
        if (response?.loan?.loanDocuments) {
          dispatch(
            loanDrilldownSetKey({
              key: "loanDocuments",
              value: response.loan.loanDocuments,
            }),
          );
        }
      },
      onCompleteFn: () => setDownloadingCertId(null),
    });
  }, [
    loanId,
    loan?.fundingShieldTransactionId,
    downloadFundingShieldCertificate,
    dispatch,
  ]);

  /* ── Build order records ────────────────────────────────────────────── */

  const orders = useMemo(() => {
    const out = [];

    // NOTE: Internal Appraisal Review is rendered as its own consolidated card
    // (AppraisalOrdersCard) above this list — it carries the order action plus
    // every existing order inline, rather than one OrderCard per order.

    // ── FLOOD CERTS — one card per subject property ──
    // WIP: temporarily disabled for everyone (incl. admins) while this
    // integration is finalized.
    // subjectProperties.forEach((property) => {
    //   out.push(
    //     applyWip(
    //       buildFloodOrder({
    //         property,
    //         onOrder: () => openFloodCertSidebar(property._id),
    //         onCheckStatus: () => handleFloodStatusCheck(property),
    //         isCheckingStatus: statusCheckingId === `flood:${property._id}`,
    //       }),
    //       { locked: true },
    //     ),
    //   );
    // });

    // ── FUNDING SHIELD — one card per loan (WIP, temporarily disabled) ──
    // out.push(
    //   applyWip(
    //     buildFundingShieldOrder({
    //       loan,
    //       onOrder: openFundingShieldSidebar,
    //       onCheckStatus: handleFundingShieldStatusCheck,
    //       onDownloadCert: handleFundingShieldCertDownload,
    //       isCheckingStatus: statusCheckingId === "funding_shield",
    //       isDownloadingCert: downloadingCertId === "funding_shield",
    //     }),
    //     { locked: true },
    //   ),
    // );

    // ── PLACEHOLDER ORDER TYPES ──
    // Not yet integrated with backend — render so the operator sees the
    // full slate of vendors that will land here. Disable the "Order"
    // button until each is wired up.
    // out.push(buildPlaceholderOrder("credit"));
    // out.push(buildPlaceholderOrder("title"));
    // out.push(buildPlaceholderOrder("background"));

    return out;
  }, [
    subjectProperties,
    loan,
    openFloodCertSidebar,
    handleFloodStatusCheck,
    handleFundingShieldStatusCheck,
    handleFundingShieldCertDownload,
    openFundingShieldSidebar,
    statusCheckingId,
    downloadingCertId,
  ]);

  /* ── Summary aggregates ─────────────────────────────────────────────── */

  const summary = useMemo(() => {
    const init = {
      total: 0,
      complete: 0,
      progress: 0,
      pending: 0,
      issue: 0,
      not_ordered: 0,
      cancelled: 0,
    };
    const tally = (status) => {
      init.total += 1;
      init[status] = (init[status] || 0) + 1;
    };
    // Internal-appraisal reviews render in their own consolidated card rather
    // than in the `orders` list, so roll them into the tiles explicitly —
    // otherwise every tile reads 0 while the card below shows live orders.
    (appraisalOrders || []).forEach((o) =>
      tally(mapAppraisalStatus(o?.status)),
    );
    // Outside-party secondaries roll in the same way.
    secondaryOrders.forEach((o) => tally(mapSecondaryStatus(o?.status)));
    orders.forEach((o) => tally(o.status));
    return init;
  }, [orders, appraisalOrders, secondaryOrders]);

  return (
    <LoanDrilldownTabPanel>
      <PrimaryContentCard
        disableLaptopFullWidth
        endContent={
          <Button
            startIcon={<AddOutlined />}
            variant="contained"
            sx={{
              textTransform: "none",
              background: grey[900],
              "&:hover": { background: grey[800] },
            }}
            disabled
          >
            New order
          </Button>
        }
        title={"Orders"}
        maxWidth="xl"
        subTitle={
          "External vendor orders and third-party services for this loan."
        }
      >
        <OrdersSummaryBar summary={summary} />

        <Flx column g={1.25}>
          <AppraisalOrdersCard
            orders={appraisalOrders || []}
            secondaryOrders={secondaryOrders}
            loanDocuments={loan?.loanDocuments || []}
            subjectProperties={subjectProperties}
            onOrder={openInternalAppraisalSidebar}
            onOrderOutside={openOutsideValuationSidebar}
            onSecondaryAction={openSecondaryAction}
            onView={setViewOrder}
            onPreviewDoc={openDocPreview}
          />
          {orders.map((o) => (
            <OrderCard key={o.id} order={o} />
          ))}
        </Flx>
      </PrimaryContentCard>

      <AppraisalOrderDetailDrawer
        order={viewOrder}
        open={Boolean(viewOrder)}
        onClose={() => setViewOrder(null)}
        showLoanLink={false}
        appraisalDocs={viewAppraisalDocs}
        onPreviewAppraisal={handlePreviewDoc}
        previewingAppraisal={previewingDoc}
      />

      <SecondaryOrderActionDialogs
        action={secondaryAction}
        dealId={loanId}
        loanDocuments={loan?.loanDocuments || []}
        onClose={() => setSecondaryAction(null)}
      />
    </LoanDrilldownTabPanel>
  );
});

/* ─────────────────────────────────────────────
   Summary tiles
   ───────────────────────────────────────────── */

const SUMMARY_TILES = [
  { key: "total", label: "Total Orders", color: null },
  { key: "complete", label: "Complete", color: green[700] },
  { key: "progress", label: "In progress", color: blue[600] },
  { key: "pending", label: "Pending", color: amber[700] },
  { key: "issue", label: "Issue", color: red[700] },
  // Edge buckets — only rendered once something lands in them, so the strip
  // isn't carrying permanently-zero tiles.
  { key: "not_ordered", label: "Not ordered", color: grey[400], whenSet: true },
  { key: "cancelled", label: "Cancelled", color: grey[500], whenSet: true },
];

const OrdersSummaryBar = memo(({ summary }) => (
  <Box
    sx={{
      // mt: 2,
      mb: 6,
      // mb: 2.5,
      py: 2,
      pr: 1,
      pl: 4,
      background: "#fff",
      border: `1px solid ${grey[200]}`,
      borderRadius: "10px",
    }}
  >
    <Box
      ac
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(100px, 1fr))`,
      }}
      wrap
      g={3}
    >
      {SUMMARY_TILES.filter((t) => !t.whenSet || (summary[t.key] || 0) > 0).map(
        (t) => (
          <Flx column g={0.4} key={t.key} sx={{ minWidth: 96 }}>
            <Flx ac g={0.5}>
              {t.color && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: t.color,
                  }}
                />
              )}
              <Txt
                sx={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: grey[500],
                }}
              >
                {t.label}
              </Txt>
            </Flx>
            <Txt
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: grey[900],
                fontFamily: "ui-monospace, Menlo, monospace",
                lineHeight: 1.2,
              }}
            >
              {summary[t.key] || 0}
            </Txt>
          </Flx>
        ),
      )}
    </Box>
  </Box>
));

/* ─────────────────────────────────────────────
   Order builders — one per order type
   ─────────────────────────────────────────────
   Each builder normalizes its source-of-truth shape into the OrderCard
   `order` contract.
*/

function buildFloodOrder({
  property,
  onOrder,
  onCheckStatus,
  isCheckingStatus,
}) {
  const result = property?.lastFloodCertResult;
  const propertyLabel =
    addressLabel(property?.address) ||
    property?.address?.streetAddress ||
    property?.address?.street ||
    "Property";
  const shortLabel =
    property?.address?.streetAddress ||
    property?.address?.street ||
    propertyLabel;
  const id = `flood:${property?._id}`;

  // No prior result → "not ordered" placeholder card with an Order button.
  if (!result) {
    return {
      id,
      type: "flood",
      name: "Flood Certification",
      category: `Flood · ${shortLabel}`,
      vendor: "Xactus",
      status: "not_ordered",
      statusDetail: "No flood certificate on file",
      timeline: [],
      viz: { type: "none" },
      cost: null,
      actions: [
        {
          label: "Order Flood Cert",
          kind: "primary",
          onClick: onOrder,
        },
      ],
    };
  }

  const followUp = result.followUp || {};
  const status = mapFloodStatus(result.status, followUp.category);
  const statusDetail = describeFloodStatus(result, followUp);
  const viz = describeFloodViz(result);
  const alert =
    status === "issue" || status === "pending"
      ? {
          tone: status === "issue" ? "issue" : "warn",
          title: floodAlertTitle(followUp.category, result.status),
          message: followUp.userMessage || result.reason || null,
        }
      : null;

  const actions = [];
  if (
    status === "pending" ||
    followUp.category === "pending_vendor_review" ||
    followUp.category === "admin_retrieve"
  ) {
    actions.push({
      label: isCheckingStatus ? "Checking…" : "Check status",
      kind: "primary",
      disabled: isCheckingStatus,
      onClick: onCheckStatus,
    });
  } else if (status === "issue") {
    actions.push({
      label:
        followUp.category === "retry_with_apn" ? "Retry with APN" : "Resolve",
      kind: "urgent",
      onClick: onOrder,
    });
  } else if (status === "complete") {
    actions.push({
      label: "Order again",
      kind: "default",
      onClick: onOrder,
    });
  } else {
    actions.push({
      label: "Order Flood Cert",
      kind: "primary",
      onClick: onOrder,
    });
  }
  actions.push({
    label: "View certificate ID",
    overflow: true,
    disabled: !result.floodCertificationId,
    onClick: () => {
      if (result.floodCertificationId) {
        // Cheap clipboard copy for the operator's convenience.
        navigator.clipboard
          ?.writeText(result.floodCertificationId)
          .catch(() => {});
      }
    },
  });

  return {
    id,
    type: "flood",
    name: "Flood Certification",
    category: `Flood · ${shortLabel}`,
    externalId: result.floodCertificationId || null,
    vendor: "Xactus",
    status,
    statusDetail,
    timeline: [
      result.pulledAt
        ? `Last attempt ${formatDate(result.pulledAt, "")}`
        : null,
      result.pulledBy ? `by ${result.pulledBy}` : null,
    ].filter(Boolean),
    viz,
    cost: null,
    actions,
    alert,
  };
}

function buildFundingShieldOrder({
  loan,
  onOrder,
  onCheckStatus,
  onDownloadCert,
  isCheckingStatus,
  isDownloadingCert,
}) {
  const txId = loan?.fundingShieldTransactionId;
  const rawStatus = loan?.fundingShieldStatus;
  const orderRecord = loan?.lastFundingShieldOrder;

  if (!txId && !rawStatus) {
    // Not yet ordered — open the modal directly.
    return {
      id: "funding_shield",
      type: "funding_shield",
      name: "Funding Shield",
      category: "Wire Verification",
      vendor: "FundingShield",
      status: "not_ordered",
      statusDetail: "Wire / closing fraud verification not yet placed",
      timeline: [],
      viz: { type: "none" },
      cost: null,
      actions: [
        {
          label: "Order Funding Shield",
          kind: "primary",
          onClick: onOrder,
        },
      ],
    };
  }

  const status = mapFundingShieldStatus(rawStatus);
  const statusDetail = describeFundingShieldStatus(rawStatus);
  const docsAvailable = Boolean(orderRecord?.transactionDocsAvailable);
  const viz = {
    type: "result",
    label: "Status",
    value: rawStatus || "Unknown",
    valueTone:
      status === "complete"
        ? "good"
        : status === "issue"
          ? "bad"
          : status === "progress"
            ? "value"
            : "neutral",
  };
  const alert =
    status === "issue"
      ? {
          tone: "issue",
          title: "Issue Reported.",
          message:
            orderRecord?.events?.find?.((e) => e?.message)?.message ||
            "FundingShield flagged a discrepancy. Halt funding until the closing agent resolves the discrepancy.",
        }
      : null;

  const actions = [];
  if (status === "progress") {
    actions.push({
      label: isCheckingStatus ? "Checking…" : "Check status",
      kind: "primary",
      disabled: isCheckingStatus,
      onClick: onCheckStatus,
    });
  } else if (status === "complete" && docsAvailable) {
    actions.push({
      label: isDownloadingCert
        ? "Downloading…"
        : "Download Guardian Certificate",
      kind: "primary",
      disabled: isDownloadingCert,
      onClick: onDownloadCert,
    });
  } else if (status === "issue") {
    actions.push({
      label: "Contact closing agent",
      kind: "urgent",
      disabled: true,
      onClick: () => {},
    });
  } else {
    actions.push({
      label: "View",
      kind: "default",
      disabled: true,
      onClick: () => {},
    });
  }
  actions.push({
    label: isCheckingStatus ? "Checking…" : "Re-check status",
    overflow: true,
    disabled: isCheckingStatus || !txId,
    onClick: onCheckStatus,
  });
  actions.push({
    label: "Place new order",
    overflow: true,
    onClick: onOrder,
  });

  return {
    id: "funding_shield",
    type: "funding_shield",
    name: "Funding Shield",
    category: "Wire Verification",
    externalId: txId ? String(txId) : null,
    vendor: "FundingShield",
    status,
    statusDetail,
    timeline: [
      orderRecord?.pulledAt
        ? `Ordered ${formatDate(orderRecord.pulledAt, "")}`
        : null,
      orderRecord?.pulledBy ? `by ${orderRecord.pulledBy}` : null,
    ].filter(Boolean),
    viz,
    cost: null,
    actions,
    alert,
  };
}

function buildPlaceholderOrder(type) {
  const meta = {
    appraisal: {
      name: "Appraisal Order",
      category: "Appraisal",
      vendor: "—",
    },
    credit: {
      name: "Credit Report",
      category: "Credit",
      vendor: "Xactus / Equifax",
    },
    title: {
      name: "Title Search & Commitment",
      category: "Title",
      vendor: "—",
    },
    background: {
      name: "Background Check",
      category: "Background",
      vendor: "LexisNexis",
    },
  }[type];
  return {
    id: `placeholder:${type}`,
    type,
    name: meta.name,
    category: meta.category,
    vendor: meta.vendor,
    status: "not_ordered",
    statusDetail: "Not yet integrated",
    timeline: [],
    viz: { type: "none" },
    cost: null,
    actions: [
      {
        label: "Order",
        kind: "default",
        disabled: true,
        onClick: () => {},
      },
    ],
  };
}

/* ─────────────────────────────────────────────
   WIP wrapper
   ─────────────────────────────────────────────
   Flags an order card as work-in-progress (renders a "WIP" badge) and, when
   `locked`, disables every action so non-admins can view status but not place
   the order while the integration is finalized.
*/
function applyWip(order, { locked }) {
  if (!order) return order;
  const next = { ...order, wip: true };
  if (locked) {
    next.locked = true;
    next.actions = (order.actions || []).map((a) => ({ ...a, disabled: true }));
  }
  return next;
}

/* ─── Status mappers ─── */

// Internal-appraisal review orders. Backend enum (InternalAppraisalOrderStatus):
// queued | assigned | in_review | completed | cancelled.
function mapAppraisalStatus(rawStatus) {
  switch (rawStatus) {
    case "completed":
      return "complete";
    case "assigned":
    case "in_review":
      return "progress";
    case "queued":
      return "pending";
    case "cancelled":
      return "cancelled";
    default:
      // Unknown/absent status — an order exists, so surface it as awaiting.
      return "pending";
  }
}

// Outside-party secondary valuation orders. Backend enum (SecondaryOrderStatus):
// requested | submitted | received | tied_out | cancelled. "Received" is the
// processor's "completed" for these, so it counts as complete.
function mapSecondaryStatus(rawStatus) {
  switch (rawStatus) {
    case "received":
    case "tied_out":
      return "complete";
    case "submitted":
      return "progress";
    case "cancelled":
      return "cancelled";
    case "requested":
    default:
      return "pending";
  }
}

function mapFloodStatus(rawStatus, followUpCategory) {
  if (rawStatus === "success") return "complete";
  if (rawStatus === "pending") return "pending";
  if (rawStatus === "skipped") return "pending";
  // Errors with retry-style follow-ups stay "issue" so the operator notices.
  if (rawStatus === "error") return "issue";
  if (followUpCategory === "pending_vendor_review") return "pending";
  return "not_ordered";
}

function describeFloodStatus(result, followUp) {
  if (followUp?.category === "auth_failed") return "Vendor auth issue";
  if (followUp?.category === "retry_with_apn")
    return "Address mismatch — APN needed";
  if (followUp?.category === "pending_vendor_review")
    return "Awaiting vendor response";
  if (followUp?.category === "fix_address")
    return "Property address incomplete";
  if (followUp?.category === "fix_borrower") return "Borrower missing on loan";
  if (followUp?.category === "vendor_outage") return "Vendor outage";
  if (result?.status === "success") return "Determination on file";
  return result?.reason || "—";
}

function describeFloodViz(result) {
  if (result?.status === "success" && result.floodZone) {
    return {
      type: "result",
      label: "Flood zone",
      value: `Zone ${result.floodZone}`,
      valueTone: result.inSpecialFloodHazardArea === true ? "warn" : "good",
    };
  }
  if (result?.status === "pending") {
    return {
      type: "result",
      label: "Status",
      value: "Pending",
      valueTone: "value",
    };
  }
  if (result?.status === "error") {
    return {
      type: "result",
      label: "Status",
      value: "Error",
      valueTone: "bad",
    };
  }
  return { type: "none" };
}

function floodAlertTitle(category, status) {
  if (category === "auth_failed") return "Vendor account issue.";
  if (category === "retry_with_apn") return "Address didn't match.";
  if (category === "pending_vendor_review") return "Vendor still reviewing.";
  if (category === "fix_address") return "Address incomplete.";
  if (category === "fix_borrower") return "Borrower missing.";
  if (category === "vendor_outage") return "Vendor outage.";
  if (status === "skipped") return "Skipped.";
  return "Action needed.";
}

function mapFundingShieldStatus(rawStatus) {
  if (!rawStatus) return "not_ordered";
  const s = String(rawStatus).toLowerCase();
  if (s.includes("approved")) return "complete";
  if (s.includes("issue")) return "issue";
  if (s.includes("rejected")) return "issue";
  if (s.includes("in process") || s.includes("submitted")) return "progress";
  return "progress";
}

function describeFundingShieldStatus(rawStatus) {
  if (!rawStatus) return "Not yet placed";
  const s = String(rawStatus).toLowerCase();
  if (s === "approved") return "Wire verified — safe to fund";
  if (s === "approved / archived") return "Verified · archived";
  if (s === "in process") return "Vendor reviewing";
  if (s === "issue reported") return "Discrepancy flagged";
  if (s === "rejected / archived") return "Cancelled";
  return rawStatus;
}

export default LoanDrilldownOrdersTab;
